import { app } from "@/main";

/**
 * Adapter for parsing Markdown-like syntax into vNodes structure
 *
 * Supported formats:
 * - Links: ["LINK"](@click:testHandler)
 * - Bold text: **BOLD TEXT** or __BOLD TEXT__
 * - Line breaks: two spaces at the end of line or backslash (\\)
 * - Plain text
 */

/**
 * Checks if a string contains Markdown-like syntax
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains Markdown-like patterns
 */
export const isMarkdownSyntax = (text) => {
  if (typeof text !== "string") {
    return false;
  }
  return (
    /\[.*?\]\(@click:.*?\)/.test(text) || // Links
    /(\*\*|__).+?\1/.test(text) || // Bold text
    / {2}$|\n.*\\$/.test(text) // Line breaks
  );
};

/**
 * Parser for links in format ["TEXT"](@click:handler)
 * @param {string} text - Text to parse
 * @returns {Array} Array of objects {type: 'link'|'text', content, handler, start, end}
 */
const parseLinks = (text) => {
  const results = [];
  const linkRegex = /\[([^\]]+)\]\(@click:([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      results.push({
        type: "text",
        content: text.substring(lastIndex, match.index),
        start: lastIndex,
        end: match.index,
      });
    }

    results.push({
      type: "link",
      content: match[1],
      handler: match[2].trim(),
      start: match.index,
      end: match.index + match[0].length,
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    results.push({
      type: "text",
      content: text.substring(lastIndex),
      start: lastIndex,
      end: text.length,
    });
  }

  if (results.length === 0) {
    results.push({
      type: "text",
      content: text,
      start: 0,
      end: text.length,
    });
  }

  return results;
};

/**
 * Parser for bold text in format **TEXT** or __TEXT__
 * @param {string} text - Text to parse
 * @param {Function} h - vNode creation function
 * @returns {Array} Array of vNodes or strings
 */
const parseBold = (text, h) => {
  const results = [];
  // Regular expression for bold text: **text** or __text__
  const boldRegex = /(\*\*|__)(.+?)\1/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index);
      if (textBefore) {
        results.push(textBefore);
      }
    }

    const boldText = match[2];
    results.push(h("strong", {}, boldText));

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    if (textAfter) {
      results.push(textAfter);
    }
  }

  return results.length > 0 ? results : [text];
};

/**
 * Parser for line breaks
 * Handles: two spaces at the end of line or backslash (\\)
 * @param {string} text - Text to parse
 * @param {Function} h - vNode creation function
 * @returns {Array} Array of vNodes or strings with <br> elements
 */
const parseLineBreaks = (text, h) => {
  const results = [];
  const lines = text.split(/\n/);

  lines.forEach((line, index) => {
    const endsWithTwoSpaces = / {2}$/.test(line);
    const endsWithBackslash = /\\$/.test(line);

    let cleanLine = line;
    if (endsWithTwoSpaces) {
      cleanLine = line.replace(/ {2}$/, "");
    } else if (endsWithBackslash) {
      cleanLine = line.replace(/\\$/, "");
    }

    if (cleanLine) {
      results.push(cleanLine);
    }

    // Add line break after current line if:
    // 1. This is not the last line in the array AND
    // 2. (Line ends with break marker OR next line is not empty)
    const isLastLine = index === lines.length - 1;
    const hasNextLine = !isLastLine && lines[index + 1] !== undefined;

    if (
      hasNextLine &&
      (endsWithTwoSpaces || endsWithBackslash || lines[index + 1].trim())
    ) {
      results.push(h("br"));
    }
  });

  return results.length > 0 ? results : [text];
};

/**
 * Creates a handler from a path (e.g., "click:openUploadOrImportTab")
 * @param {string} path - Handler path
 * @param {object} context - Execution context
 * @returns {Function} Handler function
 */
const createHandler = (path, context) => {
  return (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const parts = path.split(".");
    let value = context;

    for (const part of parts) {
      if (value && typeof value === "object" && part in value) {
        value = value[part];
      } else {
        console.warn(`Handler path "${path}" not found in context`);
        return;
      }
    }

    if (typeof value === "function") {
      value.call(context);
    } else {
      console.warn(`Handler path "${path}" is not a function`);
    }
  };
};

/**
 * Main function for parsing Markdown-like text into vNodes
 * @param {string} markdown - String with Markdown-like syntax
 * @param {object} context - Context for executing handlers (optional)
 * @returns {Array} Array of vNodes
 */
export const parseMarkdownToVNodes = (markdown, context = {}) => {
  if (typeof markdown !== "string") {
    throw new Error("Markdown adapter expects a string input");
  }

  const h = app.$createElement;
  const nodes = [];

  const linkSegments = parseLinks(markdown);

  linkSegments.forEach((segment) => {
    if (segment.type === "link") {
      const linkContent = parseBold(segment.content, h);

      nodes.push(
        h(
          "a",
          {
            href: "#",
            class: "markdown-link",
            on: {
              click: createHandler(segment.handler, context),
            },
          },
          linkContent
        )
      );
    } else {
      const textWithBreaks = parseLineBreaks(segment.content, h);

      textWithBreaks.forEach((item) => {
        if (typeof item === "string") {
          const boldParsed = parseBold(item, h);
          nodes.push(...boldParsed);
        } else {
          nodes.push(item);
        }
      });
    }
  });

  return nodes;
};

/**
 * Creates a vNode wrapper for markdown content
 * @param {string} markdown - String with Markdown-like syntax
 * @param {object} context - Context for executing handlers (optional)
 * @param {object} wrapperOptions - Wrapper options (tag, props, etc.)
 * @returns {VNode} vNode with parsed content
 */
export const createMarkdownVNode = (
  markdown,
  context = {},
  wrapperOptions = {}
) => {
  const h = app.$createElement;
  const vNodes = parseMarkdownToVNodes(markdown, context);
  const { tag = "div", props = {}, ...restOptions } = wrapperOptions;

  return h(tag, { ...props, ...restOptions }, vNodes);
};

export { parseLinks, parseBold, parseLineBreaks };
